import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import ColorThief from 'color-thief-browser';
import Model from '../Model';
import stringToColor from '../utils/stringToColor';
import ItemList from '../utils/ItemList';
import computed from '../utils/computed';
import GroupBadge from '../components/GroupBadge';
export default class User extends Model {
  constructor() {
    super(...arguments);
    _defineProperty(this, "avatarColor", null);
  }
  username() {
    return Model.attribute('username').call(this);
  }
  slug() {
    return Model.attribute('slug').call(this);
  }
  displayName() {
    return Model.attribute('displayName').call(this);
  }
  email() {
    return Model.attribute('email').call(this);
  }
  isEmailConfirmed() {
    return Model.attribute('isEmailConfirmed').call(this);
  }
  password() {
    return Model.attribute('password').call(this);
  }
  avatarUrl() {
    return Model.attribute('avatarUrl').call(this);
  }
  preferences() {
    return Model.attribute('preferences').call(this);
  }
  groups() {
    return Model.hasMany('groups').call(this);
  }
  isAdmin() {
    return Model.attribute('isAdmin').call(this);
  }
  joinTime() {
    return Model.attribute('joinTime', Model.transformDate).call(this);
  }
  lastSeenAt() {
    return Model.attribute('lastSeenAt', Model.transformDate).call(this);
  }
  markedAllAsReadAt() {
    return Model.attribute('markedAllAsReadAt', Model.transformDate).call(this);
  }
  unreadNotificationCount() {
    return Model.attribute('unreadNotificationCount').call(this);
  }
  newNotificationCount() {
    return Model.attribute('newNotificationCount').call(this);
  }
  discussionCount() {
    return Model.attribute('discussionCount').call(this);
  }
  commentCount() {
    return Model.attribute('commentCount').call(this);
  }
  canEdit() {
    return Model.attribute('canEdit').call(this);
  }
  canEditCredentials() {
    return Model.attribute('canEditCredentials').call(this);
  }
  canEditGroups() {
    return Model.attribute('canEditGroups').call(this);
  }
  canDelete() {
    return Model.attribute('canDelete').call(this);
  }
  color() {
    return computed('displayName', 'avatarUrl', 'avatarColor', (displayName, avatarUrl, avatarColor) => {
      // If we've already calculated and cached the dominant color of the user's
      // avatar, then we can return that in RGB format. If we haven't, we'll want
      // to calculate it. Unless the user doesn't have an avatar, in which case
      // we generate a color from their display name.
      if (avatarColor) {
        return "rgb(".concat(avatarColor.join(', '), ")");
      } else if (avatarUrl) {
        this.calculateAvatarColor();
        return '';
      }
      return '#' + stringToColor(displayName);
    }).call(this);
  }
  /**
   * Check whether or not the user has been seen in the last 5 minutes.
   */
  isOnline() {
    return dayjs().subtract(5, 'minutes').isBefore(this.lastSeenAt());
  }

  /**
   * Get the Badge components that apply to this user.
   */
  badges() {
    const items = new ItemList();
    const groups = this.groups();
    if (groups) {
      groups.forEach(group => {
        items.add("group".concat(group == null ? void 0 : group.id()), m(GroupBadge, {
          group: group
        }));
      });
    }
    return items;
  }

  /**
   * Calculate the dominant color of the user's avatar. The dominant color will
   * be set to the `avatarColor` property once it has been calculated.
   */
  calculateAvatarColor() {
    var _this$avatarUrl;
    const image = new Image();
    const user = this;
    image.addEventListener('load', function () {
      try {
        const colorThief = new ColorThief();
        user.avatarColor = colorThief.getColor(this);
      } catch (e) {
        // Completely white avatars throw errors due to a glitch in color thief
        // See https://github.com/lokesh/color-thief/issues/40
        if (e instanceof TypeError) {
          user.avatarColor = [255, 255, 255];
        } else {
          throw e;
        }
      }
      user.freshness = new Date();
      m.redraw();
    });
    image.crossOrigin = 'anonymous';
    image.src = (_this$avatarUrl = this.avatarUrl()) != null ? _this$avatarUrl : '';
  }

  /**
   * Update the user's preferences.
   */
  savePreferences(newPreferences) {
    const preferences = this.preferences();
    Object.assign(preferences != null ? preferences : {}, newPreferences);
    return this.save({
      preferences
    });
  }
}
flarum.reg.add('core', 'common/models/User', User);